import { canvasContext, makeCanvas, toBlob } from './canvas'
import type { LayoutConfig } from './types'

/**
 * Layout preview thumbnails, ported from
 * CreateLayoutWindow.GeneratePreviewImage().
 *
 * Rendered at the background's native size onto the background itself: each
 * slot as a filled #8DA212 rectangle with a 2px white outline and its 1-based
 * index centred in Arial Bold 48 white, then the QR box as #8DA000 with "QR" in
 * Arial Bold 24.
 *
 * The font carries a fallback stack rather than a bare `Arial`. These previews
 * are BAKED IMAGES, so a machine without the face does not degrade gracefully at
 * display time — it bakes whatever the browser substituted into the PNG. Arial
 * ships on both Windows and macOS, so this changes nothing in practice on
 * either; it is here so a machine missing it lands on Helvetica rather than on
 * a serif default.
 */

const SLOT_FILL = '#8da212'
const QR_FILL = '#8da000'
const OUTLINE = '#ffffff'

/** Arial first, so a preview baked on Windows is unchanged from before. */
const PREVIEW_FONT = 'Arial, Helvetica, "Liberation Sans", sans-serif'

/** Draws `text` centred inside the rect, matching the WPF FormattedText centring. */
function centredText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fontSize: number,
) {
  ctx.fillStyle = OUTLINE
  ctx.font = `bold ${fontSize}px ${PREVIEW_FONT}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, x + width / 2, y + height / 2)
}

export async function generateLayoutPreview(
  layout: Pick<LayoutConfig, 'slots' | 'qr'>,
  background: ImageBitmap,
  /**
   * Drawn over the slot boxes and under the QR box — the sheet's own order, so
   * this thumbnail shows what actually prints rather than a schematic that
   * disagrees with it. An opaque overlay hiding a slot here is not a fault in
   * the preview; it is what the layout does.
   */
  overlay?: ImageBitmap | null,
): Promise<Blob> {
  const canvas = makeCanvas(background.width, background.height)
  const ctx = canvasContext(canvas)

  ctx.drawImage(background, 0, 0, background.width, background.height)

  ctx.lineWidth = 2
  ctx.strokeStyle = OUTLINE

  layout.slots.forEach((slot, index) => {
    ctx.fillStyle = SLOT_FILL
    ctx.fillRect(slot.x, slot.y, slot.width, slot.height)
    ctx.strokeRect(slot.x, slot.y, slot.width, slot.height)
    centredText(ctx, String(index + 1), slot.x, slot.y, slot.width, slot.height, 48)
  })

  // Over the slots, under the QR — the compositor's order.
  if (overlay) ctx.drawImage(overlay, 0, 0, background.width, background.height)

  if (layout.qr) {
    ctx.fillStyle = QR_FILL
    ctx.fillRect(layout.qr.x, layout.qr.y, layout.qr.width, layout.qr.height)
    ctx.strokeRect(layout.qr.x, layout.qr.y, layout.qr.width, layout.qr.height)
    centredText(ctx, 'QR', layout.qr.x, layout.qr.y, layout.qr.width, layout.qr.height, 24)
  }

  return toBlob(canvas)
}
